import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@material-ui/core';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import Edit from '@material-ui/icons/Edit';
import Delete from '@material-ui/icons/Delete';
import Visibility from '@material-ui/icons/Visibility';
import Add from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  Link,
  Table,
  TableColumn,
  LinkButton,
} from '@backstage/core-components';
import { Typography } from '@material-ui/core';
import { SecretsDialog } from './SecretsDialog';
import { Secret, DialogMode } from './types';
import {
  getProviderConfig,
  getVaultName,
  getTableTitle,
  getTableSubtitle,
} from '../../utils/providerUtils';
import { executeAction } from '../../utils/apiUtils';

interface DialogState {
  isOpen: boolean;
  mode: DialogMode;
  secret: Secret | null;
}

const INITIAL_DIALOG_STATE: DialogState = {
  isOpen: false,
  mode: 'view',
  secret: null,
};

export const SecretsTable = () => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const alertApi = useApi(alertApiRef);
  const { entity } = useEntity();

  const [secrets, setSecrets] = useState<Secret[]>([]);
  const [dialogState, setDialogState] =
    useState<DialogState>(INITIAL_DIALOG_STATE);
  const [loading, setLoading] = useState(true);

  const [loadingActions, setLoadingActions] = useState<{
    [key: string]: 'view' | 'edit' | 'delete' | null;
  }>({});

  const fetchSecrets = async () => {
    try {
      const data = await executeAction(
        'list',
        null,
        entity,
        fetchApi,
        discoveryApi,
      );
      setSecrets(data.items);
    } catch (error) {
      const providerConfig = getProviderConfig(entity);

      alertApi.post({
        message: `Error loading secrets from ${providerConfig.displayName}: ${
          error instanceof Error ? error.message : String(error)
        }`,
        severity: 'error',
        display: 'transient',
      });

      setSecrets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSecrets();
    // Only fetch once on mount, not on secrets change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSecretLoading = (
    secretName: string,
    action: 'view' | 'edit' | 'delete' | null,
  ) => {
    setLoadingActions(prev => ({
      ...prev,
      [secretName]: action,
    }));
  };

  const handleCreateNew = () => {
    try {
      setDialogState({
        isOpen: true,
        mode: 'create',
        secret: null,
      });
    } catch (error) {
      alertApi.post({
        message: `Cannot create secret: ${
          error instanceof Error ? error.message : String(error)
        }`,
        severity: 'error',
        display: 'transient',
      });
    }
  };

  const handleEdit = async (secret: Secret) => {
    try {
      setSecretLoading(secret.name, 'edit');
      const data = secret.value
        ? secret
        : await executeAction('view', secret, entity, fetchApi, discoveryApi);
      setDialogState({
        isOpen: true,
        mode: 'edit',
        secret: data,
      });
    } catch (error) {
      const providerConfig = getProviderConfig(entity);
      alertApi.post({
        message: `Error loading secret for editing from ${
          providerConfig.displayName
        }: ${error instanceof Error ? error.message : String(error)}`,
        severity: 'error',
        display: 'transient',
      });
    } finally {
      setSecretLoading(secret.name, null);
    }
  };

  const handleDelete = async (secret: Secret) => {
    try {
      setSecretLoading(secret.name, 'delete');
      const data = secret.value
        ? secret
        : await executeAction('view', secret, entity, fetchApi, discoveryApi);
      setDialogState({
        isOpen: true,
        mode: 'delete',
        secret: data,
      });
    } catch (error) {
      const providerConfig = getProviderConfig(entity);
      alertApi.post({
        message: `Error loading secret for deletion from ${
          providerConfig.displayName
        }: ${error instanceof Error ? error.message : String(error)}`,
        severity: 'error',
        display: 'transient',
      });
    } finally {
      setSecretLoading(secret.name, null);
    }
  };

  const handleView = async (secret: Secret) => {
    try {
      setSecretLoading(secret.name, 'view');
      const data = secret.value
        ? secret
        : await executeAction('view', secret, entity, fetchApi, discoveryApi);
      setDialogState({
        isOpen: true,
        mode: 'view',
        secret: data,
      });
    } catch (error) {
      const providerConfig = getProviderConfig(entity);
      alertApi.post({
        message: `Error loading secret for viewing from ${
          providerConfig.displayName
        }: ${error instanceof Error ? error.message : String(error)}`,
        severity: 'error',
        display: 'transient',
      });
    } finally {
      setSecretLoading(secret.name, null);
    }
  };

  const onClose = () => {
    setDialogState(INITIAL_DIALOG_STATE);
  };

  const onSave = () => {
    setDialogState(INITIAL_DIALOG_STATE);
    fetchSecrets(); // Refresh the list

    const providerConfig = getProviderConfig(entity);

    alertApi.post({
      message: `Success on ${dialogState.mode} secret in ${providerConfig.displayName}`,
      severity: 'success',
      display: 'transient',
    });
  };

  const columns: TableColumn[] = [
    { title: 'Secret', field: 'secret', highlight: true },
    { title: 'View', field: 'view', width: '10%' },
    { title: 'Edit', field: 'edit', width: '10%' },
    { title: 'Delete', field: 'delete', width: '10%' },
  ];

  const data = secrets.map(secret => {
    const secretName = secret.name;
    const isViewLoading = loadingActions[secretName] === 'view';
    const isEditLoading = loadingActions[secretName] === 'edit';
    const isDeleteLoading = loadingActions[secretName] === 'delete';

    return {
      secret: secretName,
      view: (
        <Link
          aria-label="View"
          title={`View ${secretName}`}
          onClick={() => handleView(secret)}
          to={secretName}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isViewLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Visibility style={{ fontSize: 16 }} />
          )}
        </Link>
      ),
      edit: (
        <LinkButton
          aria-label="Edit"
          title={`Edit ${secretName}`}
          onClick={() => handleEdit(secret)}
          disabled={!secret.editable || isEditLoading}
          to={secretName}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isEditLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Edit style={{ fontSize: 16 }} />
          )}
        </LinkButton>
      ),
      delete: (
        <LinkButton
          aria-label="Delete"
          title={`Delete ${secretName}`}
          onClick={() => handleDelete(secret)}
          to={secretName}
          disabled={!secret.editable || isDeleteLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isDeleteLoading ? (
            <CircularProgress size={16} />
          ) : (
            <Delete style={{ fontSize: 16 }} />
          )}
        </LinkButton>
      ),
    };
  });

  const providerConfig = getProviderConfig(entity);

  return (
    <div style={{ position: 'relative' }}>
      <Box
        style={{
          position: 'sticky',
          zIndex: 10,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          padding: '0px 0 8px 0',
        }}
      >
        <Button
          variant="outlined"
          color="default"
          startIcon={<RefreshIcon />}
          onClick={fetchSecrets}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleCreateNew}
        >
          Create New Secret
        </Button>
      </Box>

      <Table
        title={getTableTitle(entity)}
        subtitle={getTableSubtitle(entity)}
        columns={columns}
        data={data}
        isLoading={loading}
        options={{
          padding: 'dense',
          pageSize: 10,
          emptyRowsWhenPaging: false,
          search: true,
          toolbar: true,
        }}
        emptyContent={
          <Box style={{ textAlign: 'center', padding: '15px' }}>
            <Typography variant="body1">
              No secrets found for {entity.metadata.name}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8 }}
            >
              Provider: {providerConfig.displayName} | Store:{' '}
              {getVaultName(entity)}
            </Typography>
          </Box>
        }
      />

      <SecretsDialog
        open={dialogState.isOpen}
        mode={dialogState.mode}
        secret={dialogState.secret}
        onClose={onClose}
        onSave={onSave}
      />
    </div>
  );
};
